import React, { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { ClawMarks } from "./WolfMark";

const container = {
  hidden: {},
  show: { transition: { staggerChildren: 0.12, delayChildren: 0.35 } },
};
const lineUp = {
  hidden: { y: "110%" },
  show: { y: "0%", transition: { duration: 1, ease: [0.16, 1, 0.3, 1] } },
};
const fade = {
  hidden: { opacity: 0, y: 20 },
  show: { opacity: 1, y: 0, transition: { duration: 0.9, ease: [0.16, 1, 0.3, 1] } },
};

const MaskLine = ({ children, className }) => (
  <span className="reveal-line">
    <motion.span variants={lineUp} className={`block ${className || ""}`}>
      {children}
    </motion.span>
  </span>
);

export const Hero = ({ onShop }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const wolfY = useTransform(scrollYProgress, [0, 1], [0, 160]);
  const athleteY = useTransform(scrollYProgress, [0, 1], [0, 90]);
  const athleteScale = useTransform(scrollYProgress, [0, 1], [1, 1.08]);
  const textY = useTransform(scrollYProgress, [0, 1], [0, -60]);

  return (
    <section id="collections-top" ref={ref} className="relative min-h-screen w-full overflow-hidden concrete">
      {/* Background layers */}
      <div className="absolute inset-0 bg-[#050505]" />
      <img
        src="/generated/texture_smoke.png"
        alt=""
        aria-hidden="true"
        className="absolute inset-0 w-full h-full object-cover opacity-30 mix-blend-screen pointer-events-none"
      />
      {/* Faded wolf head watermark */}
      <motion.div style={{ y: wolfY }} className="absolute inset-0 flex items-center justify-center pointer-events-none">
        <img
          src="/generated/wolf_white.png"
          alt=""
          aria-hidden="true"
          className="w-[720px] max-w-[90vw] object-contain opacity-[0.045]"
        />
      </motion.div>
      {/* Claw marks */}
      <ClawMarks className="absolute left-[6%] top-[46%] w-24 md:w-36 text-white/[0.06] pointer-events-none" />

      {/* Content grid */}
      <div className="relative z-10 mx-auto max-w-[1600px] px-6 md:px-10 grid grid-cols-12 gap-6 min-h-screen pt-28 md:pt-24">
        {/* LEFT — copy */}
        <motion.div
          style={{ y: textY }}
          variants={container}
          initial="hidden"
          animate="show"
          className="col-span-12 md:col-span-5 flex flex-col justify-center py-10 md:py-0"
        >
          <motion.span variants={fade} className="font-sans text-[11px] tracking-[0.45em] text-white/50 mb-6">
            LONE WOLF GYM GEAR
          </motion.span>

          <h1 className="font-display font-black uppercase tracking-normal leading-[0.86] text-white text-6xl sm:text-7xl lg:text-8xl">
            <MaskLine>This is not</MaskLine>
            <MaskLine>a team sport.</MaskLine>
          </h1>

          <motion.div variants={fade} className="mt-8 h-px w-16 bg-white/40" />

          <motion.p variants={fade} className="mt-8 font-sans text-lg text-white/70 leading-relaxed">
            Built for the few.
            <br />
            Not the followers.
          </motion.p>

          <motion.div variants={fade} className="mt-10">
            <button
              data-testid="shop-collection-button"
              onClick={onShop}
              className="group relative inline-flex items-center gap-5 border border-white/25 pl-8 pr-6 py-4 overflow-hidden"
            >
              <span className="absolute inset-0 bg-white translate-y-full group-hover:translate-y-0 transition-transform duration-500 ease-out" />
              <span className="relative font-sans text-xs tracking-[0.28em] text-white group-hover:text-black transition-colors duration-500">
                SHOP COLLECTION
              </span>
              <ArrowRight className="relative w-4 h-4 text-white group-hover:text-black transition-all duration-500 group-hover:translate-x-1" />
            </button>
          </motion.div>

          <motion.p variants={fade} className="mt-14 font-sans text-sm text-white/40 leading-relaxed max-w-xs">
            The original pioneers of bodybuilding.
            <br />
            You and the steel.
            <br />
            Nothing else.
          </motion.p>
        </motion.div>

        {/* RIGHT — athlete */}
        <div className="col-span-12 md:col-span-7 relative flex items-end justify-center">
          <div className="absolute inset-x-0 top-0 h-40 spotlight pointer-events-none" />
          <motion.img
            src="/generated/hero_athlete_cutout.png"
            alt="Athlete wearing the matte black Lone Wolf performance shirt"
            style={{ y: athleteY, scale: athleteScale }}
            initial={{ opacity: 0, scale: 1.06 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1.6, ease: [0.16, 1, 0.3, 1], delay: 0.2 }}
            className="relative z-10 h-[62vh] md:h-[88vh] w-auto object-contain object-bottom drop-shadow-[0_40px_80px_rgba(0,0,0,0.9)]"
          />
          {/* fade the bottom into page */}
          <div className="absolute bottom-0 inset-x-0 h-40 bg-gradient-to-t from-[#050505] to-transparent z-20 pointer-events-none" />
        </div>
      </div>

      {/* scroll cue */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.6, duration: 1 }}
        className="absolute bottom-6 left-1/2 -translate-x-1/2 z-30 flex flex-col items-center gap-2"
      >
        <span className="font-sans text-[10px] tracking-[0.4em] text-white/40">SCROLL</span>
        <motion.div
          animate={{ y: [0, 8, 0] }}
          transition={{ repeat: Infinity, duration: 1.8, ease: "easeInOut" }}
          className="h-8 w-px bg-white/30"
        />
      </motion.div>
    </section>
  );
};

export default Hero;
